import React, { useState, useEffect } from "react";
import { formatDate } from "../../utils/patientUtils";
import { Dialog, DialogContent, Typography, Divider } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from '@mui/icons-material/Upload';
import html2pdf from "html2pdf.js";
import PdfTemplate from "../pdfTemplate/pdfTemplate";
import axios from "axios";
import config from "../../config";
import { nextSteps, handleSetDR, handleSetReferral, getInferenceFromData } from "../../utlis";
import { db } from "../../db";
import "./ExportDialog.css";

const ExportDialog = ({ Open, close, reports }) => {
  const [exportAll, setExportAll] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [optometristRole, setOptometristRole] = useState(null);
  const [blobUrls, setBlobUrls] = useState([]); // Track blob URLs for cleanup

  const header = {
    Authorization: "Token " + localStorage.getItem("optha-token"),
  };

  useEffect(() => {
    // Get the optometrist role from localStorage
    const role = localStorage.getItem("optometrist_role");
    setOptometristRole(role);
  }, []);

  // Cleanup blob URLs when component unmounts or reportData changes
  useEffect(() => {
    return () => {
      blobUrls.forEach(url => {
        if (url && url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(url);
          } catch (error) {
            console.warn('Error revoking blob URL:', error);
          }
        }
      });
    };
  }, [blobUrls]);

  const handleSelectAll = () => {
    setExportAll(!exportAll);
    setSelectedReports(!exportAll ? reports.map((r) => r.uuid) : []);
  };

  const handleReportSelection = (uuid) => {
    setSelectedReports((prevSelected) =>
      prevSelected.includes(uuid)
        ? prevSelected.filter((id) => id !== uuid)
        : [...prevSelected, uuid]
    );
  };


  // Convert IndexedDB image (Blob or { data: ArrayBuffer, type }) to blob URL
  const indexDBImageToBlobURL = (imageData) => {
    if (!imageData) return null;
    try {
      if (imageData.data && imageData.type) {
        const blob = new Blob([imageData.data], { type: imageData.type });
        return URL.createObjectURL(blob);
      }
      return URL.createObjectURL(imageData);
    } catch (e) {
      console.warn("Export: failed to create blob URL for image", e);
      return null;
    }
  };

  // Convert blob URL to data URL so PDF (html2pdf) can embed images
  const blobURLToDataURL = (url) => {
    if (!url || !url.startsWith("blob:")) return Promise.resolve(url);
    return fetch(url)
      .then((r) => r.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .catch((e) => {
        console.warn("Export: blob to data URL failed", e);
        return url;
      });
  };

  const fetchReportFromIndexedDB = async (uuid) => {
    const report = await db.reports.get(uuid);
    if (!report) return null;
    const patient = await db.patients.get(report.patient_id);
    if (!patient) return null;
    const leftUrl = indexDBImageToBlobURL(report.left_image);
    const rightUrl = indexDBImageToBlobURL(report.right_image);
    [leftUrl, rightUrl].filter(Boolean).forEach((url) => setBlobUrls((prev) => [...prev, url]));
    return {
      ...report,
      patient,
      uuid: report.id,
      files: { left: leftUrl, right: rightUrl },
    };
  };

  function imagetoBase64(imageURL1, imageURL2) {
    return Promise.all([fetch(imageURL1), fetch(imageURL2)])
      .then((responses) => Promise.all(responses.map((response) => response.blob())))
      .then((blobs) => {
        // Convert blobs to data URLs instead of blob URLs
        return Promise.all(blobs.map((blob) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Failed to convert to data URL'));
            reader.readAsDataURL(blob);
          });
        }));
      })
      .then((dataURLs) => {
        return {
          left: dataURLs[0],
          right: dataURLs[1],
        };
      })
      .catch((error) => {
        console.error("Error converting images:", error);
        return { left: null, right: null };
      });
  }
  const fetchReportsData = async () => {
    if (selectedReports.length === 0) {
      alert("Please select at least one report to export.");
      return;
    }
    setLoading(true);
    const fetchedReports = [];
    
    // Check if we're offline
    const isOffline = !navigator.onLine;
    
    if (isOffline) {
      // Fetch from IndexedDB when offline
      for (const uuid of selectedReports) {
        try {
          const report = await fetchReportFromIndexedDB(uuid);
          if (report) fetchedReports.push(report);
        } catch (error) {
          console.error(`Error fetching report ${uuid} from IndexedDB:`, error);
        }
      }
    } else {
      // Online: try API first; for unsynced (sync record) reports fall back to IndexedDB
      for (const uuid of selectedReports) {
        try {
          const url = `${config.API_BASE_URL}/api/reports/getreport/?uuid=${uuid}`;
          const response = await axios.get(url, { headers: header });
          if (response.data.results && response.data.results.length > 0) {
            fetchedReports.push(response.data.results[0]);
          } else {
            // Report not on server (e.g. unsynced from sync data page) – load from IndexedDB
            const report = await fetchReportFromIndexedDB(uuid);
            if (report) fetchedReports.push(report);
          }
        } catch (error) {
          // Network/API error – try IndexedDB (e.g. unsynced report)
          try {
            const report = await fetchReportFromIndexedDB(uuid);
            if (report) fetchedReports.push(report);
          } catch (idbError) {
            console.error(`Error fetching report ${uuid}:`, error);
          }
        }
      }
    }

    const updatedReports = await Promise.all(
      fetchedReports.map(async (report) => {
        let blobURLs;
        const filesAlreadyBlobUrls = report.files?.left?.startsWith?.('blob:') || report.files?.right?.startsWith?.('blob:');
        const isOffline = !navigator.onLine;
        if (isOffline || filesAlreadyBlobUrls) {
          // From IndexedDB: we have blob URLs – convert to data URLs so PDF can show images
          const left = report.files?.left ?? null;
          const right = report.files?.right ?? null;
          blobURLs = {
            left: left ? await blobURLToDataURL(left) : null,
            right: right ? await blobURLToDataURL(right) : null
          };
        } else {
          // Online + from API: convert image URLs to data URLs
          blobURLs = await imagetoBase64(report.files?.left, report.files?.right);
        }
        
        // Handle missing images and single image mode
        const hasLeftImage = blobURLs.left && blobURLs.left !== 'null';
        const hasRightImage = blobURLs.right && blobURLs.right !== 'null';
        const singleImageReason = report.single_image_reason;
        
        // Determine inference text based on image availability and single image mode
        let leftInference, rightInference;
        
        if (!hasLeftImage && singleImageReason) {
          leftInference = `Image Not Available: ${singleImageReason}`;
        } else if (!hasLeftImage) {
          leftInference = "Image Not Available";
        } else {
          // Use feedback corrected inference if available, otherwise use ML model inference
          leftInference = report?.feedback_data?.left_eye_correct_inference || handleSetDR(
            report?.ml_model?.classification?.left,
            report?.ml_model?.gradability?.left,
            report?.ml_model?.model_outputs?.left
          );
        }
        
        if (!hasRightImage && singleImageReason) {
          rightInference = `Image Not Available: ${singleImageReason}`;
        } else if (!hasRightImage) {
          rightInference = "Image Not Available";
        } else {
          // Use feedback corrected inference if available, otherwise use ML model inference
          rightInference = report?.feedback_data?.right_eye_correct_inference || handleSetDR(
            report?.ml_model?.classification?.right,
            report?.ml_model?.gradability?.right,
            report?.ml_model?.model_outputs?.right
          );
        }
        
        // Use feedback next steps if available, otherwise calculate from inferences
        let date, message;
        if (report?.feedback_data?.next_steps_message) {
          // Extract date and message from feedback next steps
          const nextStepsParts = report.feedback_data.next_steps_message.split(' on ');
          message = nextStepsParts[0] || '';
          date = report.feedback_data.next_steps_date || nextStepsParts[1] || '';
        } else {
          // Get DME information from report
          const leftDME = report?.ml_model?.model_outputs?.left?.drdme?.DME_grade || null;
          const rightDME = report?.ml_model?.model_outputs?.right?.drdme?.DME_grade || null;
          const operatorOverride = report?.ml_model?.operator_override || { left: { fundus: false, eye: false }, right: { fundus: false, eye: false } };
          
          const nextStepsResult = nextSteps(leftInference, rightInference, leftDME, rightDME, operatorOverride);
          date = nextStepsResult.date;
          message = nextStepsResult.message;
        }
        return {
          ...report,
          files: {
            left: hasLeftImage ? blobURLs.left : null,
            right: hasRightImage ? blobURLs.right : null,
          },
          date,
          message,
          leftInference,
          rightInference,
        };
      })
    );

    setReportData(updatedReports);
    setLoading(false);
  };


  const generatePDF = async () => {
    const element = document.getElementById("pdf-content");
    if (!element) {
      alert("Error: No PDF content to generate.");
      return;
    }

    // Generate filename based on patient ID and report number
    let filename = "Report";
    if (reportData.length > 0 && reports.length > 0) {
      const firstReport = reportData[0];
      const patientId = firstReport.patient?.other_id || firstReport.patient?.abha_id || "Unknown";
      const reportNumber = reports.findIndex((r) => r.uuid === firstReport.uuid) + 1;
      
      if (reportData.length === 1) {
        // Single report: Report_patientID_reportNumber.pdf
        filename = `Report_${patientId}_${reportNumber}.pdf`;
      } else {
        // Multiple reports: Report_patientID_multiple.pdf
        filename = `Report_${patientId}_multiple.pdf`;
      }
    } else {
      filename = "Report_Unknown.pdf";
    }

    html2pdf()
      .from(element)
      .set({
        filename: filename,
        image: { type: "jpeg", quality: 1.0 },
        html2canvas: { scale: 3, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        timeout: 10000  // <-- increase timeout to wait for images
      })
      .save();
  };

  useEffect(() => {
    if (reportData.length > 0) {
      const doGenerate = async () => {
        // setTimeout(() => generatePDF(), 1000);
        generatePDF()
      };
      doGenerate();
    }
  }, [reportData]);
  const reports_reverse = [...reports].reverse();
  return (
    <Dialog open={Open} onClose={close} className="export-dialog" >
      <DialogContent className="dialog-content">
        <div className="dialog-header">
          <Typography className="export-dialog-heading" variant="h6" component="div">
            <UploadIcon className="export-dialog-heading-icon" />
            Export
          </Typography>
          <CloseIcon className="close-icon" onClick={close} />
        </div>

        <div className="report-list">
          <div className="report-item select-all">
            <Checkbox checked={exportAll} onChange={handleSelectAll}
              sx={{
                "&.Mui-checked": {
                  color: "#0D1E83",
                },
              }} />
            <span className="report-text">Export All Reports</span>
          </div>
          {reports_reverse.map((report, index) => (
            <div key={report.uuid} className="report-item item-list">
              <Checkbox
                checked={selectedReports.includes(report.uuid)}
                onChange={() => handleReportSelection(report.uuid)}
                sx={{
                  "&.Mui-checked": {
                    color: "#0D1E83",
                  },
                }}
              />
              <div>
                <Typography className="report-name" variant="body1">
                  {`Report ${reports.length - index}${index === 0 ? " (Latest Report)" : ""}`}
                </Typography>
                <Typography className="report-date" variant="body2">
                  Visit Date: {formatDate(report.visited_at)}
                </Typography>
              </div>
            </div>
          ))}
        </div>
        <div className="bottom-action-bar">
          <div className="action-button left" onClick={close}>
            Close
          </div>
          <Divider orientation="vertical" flexItem />
          <div
            className={`action-button right`}
            onClick={selectedReports.length === 0 ? null : fetchReportsData}
          >
            {loading ? "Generating PDF..." : `Export (${selectedReports.length})`}
          </div>
        </div>


        {/* <Button
        className="export-button"
        variant="contained"
        // color="primary"
        sx={{
          backgroundColor:"#0D1E83"
        }}
        
        fullWidth
        onClick={fetchReportsData}
        disabled={loading}
      >
        {loading ? "Generating PDF..." : "Export as PDF"}
      </Button> */}

        <div style={{ display: "none" }}>
          <div id="pdf-content">
            {reportData.map((report, index) => (
              <PdfTemplate
                key={index}
                downloadFlag={false}
                fileName={`Report_${report.uuid}`}
                heading="Diabetic Retinopathy Screening Report"
                reportindex={reports.findIndex((r) => r.uuid === report.uuid) + 1}
                visitDate={formatDate(report.created_at || report.visited_at)}
                patientDetails={[
                  // { label: "Patient Name", value: report.patient?.name },
                  {
                    label: "Patient ID",
                    value:
                      report.patient?.other_id || report.patient?.abha_id,
                  },
                  { label: "Age", value: `${report.patient?.age} Y` },
                  { label: "Gender", value: report.patient?.sex },
                  { 
                    label: "Diabetes", 
                    value: (() => {
                      const diabetes = report?.diabetes || '';
                      if (diabetes === "Yes" && (report?.diabetes_duration_years || report?.diabetes_duration_months)) {
                        const years = report?.diabetes_duration_years || 0;
                        const months = report?.diabetes_duration_months || 0;
                        const parts = [];
                        if (years) {
                          parts.push(`${years}year${years > 1 ? 's' : ''}`);
                        }
                        if (months) {
                          parts.push(`${months}month${months > 1 ? 's' : ''}`);
                        }
                        if (parts.length > 0) {
                          return `${diabetes}(${parts.join(', ')})`;
                        }
                      }
                      return diabetes;
                    })()
                  },
                  { 
                    label: "Patient Reported Vision Issues", 
                    value: (() => {
                      // Try new structure first
                      const distantLeft = report?.visual_acuity_distant_left?.trim() || '';
                      const distantRight = report?.visual_acuity_distant_right?.trim() || '';
                      const nearLeft = report?.visual_acuity_near_left?.trim() || '';
                      const nearRight = report?.visual_acuity_near_right?.trim() || '';
                      
                      if (distantLeft || distantRight || nearLeft || nearRight) {
                        const parts = [];
                        if (distantLeft || distantRight) {
                          const distantParts = [];
                          if (distantLeft) distantParts.push(`${distantLeft} Left`);
                          if (distantRight) distantParts.push(`${distantRight} Right`);
                          parts.push(`Distant Vision - ${distantParts.join(', ')}`);
                        }
                        if (nearLeft || nearRight) {
                          const nearParts = [];
                          if (nearLeft) nearParts.push(`${nearLeft} Left`);
                          if (nearRight) nearParts.push(`${nearRight} Right`);
                          parts.push(`Near Vision - ${nearParts.join(', ')}`);
                        }
                        return parts.join(', ');
                      }
                      
                      // Fallback to old structure
                      const left = report?.visual_acuity_left?.trim() || '';
                      const right = report?.visual_acuity_right?.trim() || '';
                      if (left && right) {
                        return `Left: ${left}, Right: ${right}`;
                      } else if (left) {
                        return `Left: ${left}`;
                      } else if (right) {
                        return `Right: ${right}`;
                      } else if (report?.vision_issues?.trim()) {
                        return report.vision_issues; // Fallback to old format
                      }
                      return "Unknown";
                    })()
                  },

                ]}
                // url={`https://example.com/report/${report.uuid}`}
                images={{ 
                  img1: report.files?.left || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgQXZhaWxhYmxlPC90ZXh0Pgo8L3N2Zz4K", 
                  img2: report.files?.right || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgQXZhaWxhYmxlPC90ZXh0Pgo8L3N2Zz4K"
                }}
                leftInference={report.leftInference || handleSetDR(
                  report?.ml_model?.classification?.left,
                  report?.ml_model?.gradability?.left,
                  report?.ml_model?.model_outputs?.left
                )}
                rightInference={report.rightInference || handleSetDR(
                  report?.ml_model?.classification?.right,
                  report?.ml_model?.gradability?.right,
                  report?.ml_model?.model_outputs?.right
                )}
                leftDME={report?.ml_model?.model_outputs?.left?.drdme?.DME_grade || null}
                rightDME={report?.ml_model?.model_outputs?.right?.drdme?.DME_grade || null}
                operatorOverride={report?.ml_model?.operator_override || { left: { fundus: false, eye: false }, right: { fundus: false, eye: false } }}
                required={handleSetReferral(
                  report?.feedback_data?.left_eye_correct_inference || getInferenceFromData(report?.ml_model?.classification?.left, report?.ml_model?.model_outputs?.left),
                  report?.feedback_data?.right_eye_correct_inference || getInferenceFromData(report?.ml_model?.classification?.right, report?.ml_model?.model_outputs?.right),
                  report?.single_image_reason,
                  report?.ml_model?.operator_override || { left: { fundus: false, eye: false }, right: { fundus: false, eye: false } },
                  { left: report?.ml_model?.model_outputs?.left?.drdme?.DME_grade || null, right: report?.ml_model?.model_outputs?.right?.drdme?.DME_grade || null }
                )}
                singleImageReason={report.single_image_reason}
                treatmentProvided={report?.feedback_data?.treatment_provided}
                furtherReferralRequired={report?.feedback_data?.further_referral_required}
                // notes={report.notes || "No additional notes available."}
                nextSteps={
                  // report.ml_model?.nextSteps ||
                  // "Referral required within 3-6 months and suggest sugar monitoring."
                  [null, report.message]
                }
                // hospitalVisit={
                //   report.hospitalVisit || "Regular follow-up required."
                // }
                disclaimer={"MadhuNetrAI, an AI-based Diabetic Retinopathy Screening application, has been developed under the guidance and supervision of qualified doctors from AIIMS New Delhi and AIIMS Rishikesh. However, the AI interpretation is only a prediction, and the final decision rests with the attending doctor. The E-Health division of the Ministry of Health and Family Welfare & Wadhwani AI—the AI Unit of the Lords Education and Health Society (LEHS)—disclaims any liability, claims, or disputes arising from the use of this application. The information, including but not limited to text, graphics, images, and other material contained in this application, are for informational purposes only. No material on this application is intended to be a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of a qualified healthcare provider and never disregard professional medical advice or delay in seeking it because of something you have read or seen on this application."}
              />
            ))}
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialog;
